"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { RiHourglassLine } from "react-icons/ri";
import dynamic from "next/dynamic";
import { useParams } from "next/navigation";
import { useProgressNavigation } from "@/components/progress-bar";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui";
import Button from "@/components/ui/button";
import { useUser } from "@/contexts/user-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { useModule } from "@/hooks/use-module";
import { clinicClockFor } from "@/lib/clinic-clock";
import { formatDate } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { clickableText, cx } from "@/lib/tailwind-utils";
import { WaitlistEntry } from "@/types/waitlist";
import { buildWaitlistBookingUrl } from "./waitlist-booking-url";
import { WaitlistMatchEntryDescription } from "./waitlist-entry-description";
import { getMatchingWaitlistEntries } from "./waitlist-matching";
import { useWaitlistForProvidersDate } from "@/query/waitlist-query";
const WaitlistBookingMessageDialog = dynamic(() => import("./waitlist-booking-message-dialog"), {
  ssr: false
});
interface WaitlistMatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerIds: string[];
  date: Date;
  serviceId?: string;
  locationId?: string;
  timezone?: string;
  cancelledAppointment?: boolean;
}
export default function WaitlistMatchDialog(t0) {
  const $ = _c(38);
  const {
    open,
    onOpenChange,
    providerIds,
    date,
    serviceId,
    locationId,
    timezone,
    cancelledAppointment: t1
  } = t0;
  const cancelledAppointment = t1 === undefined ? false : t1;
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const {
    tenant
  } = useParams();
  const {
    currentUser
  } = useUser();
  const hasTexting = useModule("TEXTING");
  const hasScheduling = useModule("SCHEDULING");
  const hasWaitlistModule = useModule("WAITLIST");
  let t2;
  if ($[0] !== currentUser || $[1] !== hasWaitlistModule) {
    t2 = hasWaitlistModule && hasPermission(currentUser, PERMISSIONS.SCHEDULING_WAITLIST_MANAGE);
    $[0] = currentUser;
    $[1] = hasWaitlistModule;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const canManageWaitlist = t2;
  let t3;
  if ($[3] !== currentUser) {
    t3 = hasPermission(currentUser, PERMISSIONS.SCHEDULING_APPOINTMENTS_WRITE);
    $[3] = currentUser;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const canBook = t3;
  const canMessagePatient = hasTexting && hasScheduling && canManageWaitlist;
  const [messageSelection, setMessageSelection] = useState(null);
  const clinicClockNow = useClinicClock();
  const clock = timezone ? clinicClockFor(timezone) : clinicClockNow;
  const dateStr = clock.dayOf(date);
  const dayLabel = formatDate(dateStr, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
  let t4;
  if ($[5] !== locationId) {
    t4 = locationId ? [locationId] : undefined;
    $[5] = locationId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: entries,
    isLoading,
    isError,
    refetch
  } = useWaitlistForProvidersDate(providerIds.length ? providerIds : undefined, dateStr, open && canManageWaitlist, t4);
  const waitingEntries = getMatchingWaitlistEntries(entries, date, serviceId, locationId, providerIds, clock);
  let t5;
  if ($[7] !== canBook || $[8] !== canManageWaitlist || $[9] !== date || $[10] !== locationId || $[11] !== navigateWithProgress || $[12] !== onOpenChange) {
    t5 = entry => {
      if (!canBook || !canManageWaitlist) {
        return;
      }
      onOpenChange(false);
      navigateWithProgress(buildWaitlistBookingUrl(entry, {
        start: date,
        locationId: locationId ?? entry.locationId
      }));
    };
    $[7] = canBook;
    $[8] = canManageWaitlist;
    $[9] = date;
    $[10] = locationId;
    $[11] = navigateWithProgress;
    $[12] = onOpenChange;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleBook = t5;
  if (!canManageWaitlist) {
    return null;
  }
  const T0 = Dialog;
  const t6 = open && !messageSelection;
  const T1 = DialogContent;
  const t7 = "sm:max-w-md max-h-[60dvh]";
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DialogHeader><DialogTitle className="flex items-center gap-2"><RiHourglassLine className="h-5 w-5 text-status-warning-text" />Patients on Waitlist</DialogTitle></DialogHeader>;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const T2 = DialogBody;
  let t9;
  if ($[15] !== cancelledAppointment) {
    t9 = cancelledAppointment && <p className="mb-4 text-sm text-foreground-secondary">The appointment was cancelled. Offer the newly available appointment to a waiting patient. Confirm availability for all requested services before booking. No messages are sent automatically.</p>;
    $[15] = cancelledAppointment;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = isLoading ? <p className="py-4 text-center text-sm text-foreground-secondary">Checking waitlist...</p> : isError || entries === null ? <div className="py-4 text-center text-sm text-foreground-secondary"><p>Unable to check the waitlist. Please try again.</p><button type="button" className={cx(clickableText, "mt-2")} onClick={() => void refetch()}>Retry</button></div> : !waitingEntries || waitingEntries.length === 0 ? <p className="py-4 text-center text-sm text-foreground-secondary">No patients waiting on {dayLabel}</p> : <div className="flex flex-col space-y-4"><p className="mb-1 text-caption text-foreground-secondary">{`${waitingEntries.length} patient${waitingEntries.length !== 1 ? "s" : ""} waiting for `}{dayLabel}</p><p className="text-caption text-foreground-secondary">These patients have matching preferences. Availability for the full appointment has not been verified.</p>{waitingEntries.map(entry_0 => <div key={entry_0.id} className="border-b border-border mb-1"><WaitlistMatchEntryDescription entry={entry_0} actions={<>{canBook && <Button type="button" variant="primary" size="sm" onClick={() => handleBook(entry_0)}>Book</Button>}{canMessagePatient && <Button type="button" variant="secondary" size="sm" onClick={() => {
          setMessageSelection({
            tenant,
            entries: [entry_0]
          });
        }}>Message</Button>}</>} /></div>)}</div>;
  let t11;
  if ($[17] !== T2 || $[18] !== t10 || $[19] !== t9) {
    t11 = <T2>{t9}{t10}</T2>;
    $[17] = T2;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== T1 || $[22] !== t11 || $[23] !== t8) {
    t12 = <T1 className={t7}>{t8}{t11}</T1>;
    $[21] = T1;
    $[22] = t11;
    $[23] = t8;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== T0 || $[26] !== onOpenChange || $[27] !== t12 || $[28] !== t6) {
    t13 = <T0 open={t6} onOpenChange={onOpenChange}>{t12}</T0>;
    $[25] = T0;
    $[26] = onOpenChange;
    $[27] = t12;
    $[28] = t6;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== canMessagePatient || $[31] !== messageSelection || $[32] !== onOpenChange || $[33] !== tenant) {
    t14 = messageSelection && messageSelection.tenant === tenant && canMessagePatient && <WaitlistBookingMessageDialog entries={messageSelection.entries} onClose={() => {
      setMessageSelection(null);
      onOpenChange(false);
    }} />;
    $[30] = canMessagePatient;
    $[31] = messageSelection;
    $[32] = onOpenChange;
    $[33] = tenant;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t13 || $[36] !== t14) {
    t15 = <>{t13}{t14}</>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
}
export { getMatchingWaitlistEntries };